/**
 * Safe HTML Sanitizer for Search Results
 *
 * Backend returns search results with HTML tags for highlighting (<b>match</b>).
 * This function sanitizes the HTML to:
 * - Allow ONLY safe formatting tags: <b>, <i>, <em>, <strong>
 * - Block ALL potentially dangerous tags: <script>, <iframe>, <img>, <a>, etc.
 * - Strip ALL event handlers: onclick, onerror, etc.
 * - Prevent XSS injection while preserving formatting
 *
 * Implementation: Simple regex-based approach (safe for our limited use case)
 * We're NOT using dangerouslySetInnerHTML - we sanitize first, then React renders safely
 */

// Whitelist of allowed tags (formatting only, no attributes)
const ALLOWED_TAGS = ['b', 'i', 'em', 'strong'];

/**
 * Sanitize HTML string to allow only safe formatting tags
 * @param {string} html - HTML string from backend search results
 * @returns {string} Sanitized HTML with only allowed tags
 */
export function sanitizeSearchHTML(html) {
  // Handle null/undefined/empty
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Step 0: Remove dangerous tags AND their content completely
  // Remove <script>...</script> including content
  let sanitized = html.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '');

  // Remove <style>...</style> including content
  sanitized = sanitized.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '');

  // Step 1: Remove all tags that are NOT in the whitelist
  // This regex finds all HTML tags
  sanitized = sanitized.replace(/<\/?([a-z][a-z0-9]*)\b[^>]*>/gi, (match, tagName) => {
    const tag = tagName.toLowerCase();

    // Check if tag is in whitelist
    if (ALLOWED_TAGS.includes(tag)) {
      // Keep only the tag itself, strip all attributes
      // Detect if it's a closing tag
      if (match.startsWith('</')) {
        return `</${tag}>`;
      } else {
        return `<${tag}>`;
      }
    }

    // Not in whitelist - remove the tag but keep the content
    return '';
  });

  // Step 2: Remove any remaining dangerous patterns
  // Remove javascript: protocol
  sanitized = sanitized.replace(/javascript:/gi, '');

  // Remove data: protocol (could be used for XSS)
  sanitized = sanitized.replace(/data:/gi, '');

  // Remove on* event handlers (onclick, onerror, etc.) that might have slipped through
  sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '');

  return sanitized;
}

/**
 * Convert sanitized HTML to React-safe format
 * Returns an object that can be used with dangerouslySetInnerHTML
 * ONLY after sanitization
 *
 * @param {string} html - HTML string (should be sanitized first!)
 * @returns {Object} Object for dangerouslySetInnerHTML: { __html: string }
 */
export function createSafeMarkup(html) {
  const sanitized = sanitizeSearchHTML(html);
  return { __html: sanitized };
}
